import React, { useState } from 'react';
import './LoginSignUp.css';
import { Spinner, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useUserAuth } from '../Context/UserAuthContext';
import logo from '../images/lawbot.jpg';

const SignUP = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { signUp } = useUserAuth();
  const navigate = useNavigate();

  const getErrorMessage = (error) => {
    const message = error.message || error;
    if (message.includes('email-already-in-use')) return 'This email is already registered. Please login instead.';
    if (message.includes('invalid-email')) return 'Please enter a valid email address.';
    if (message.includes('weak-password')) return 'Password is too weak. Use at least 6 characters with letters and numbers.';
    if (message.includes('network')) return 'Network error. Please check your internet connection.';
    return 'Sign up failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validation
    if (!email || !password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (!email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password);
      setSuccess(true);
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Left Side - Image */}
      <div className="auth-image-side">
        <div className="auth-image-content">
          <h2>Join LawBot Today</h2>
          <p>
            Create your account to access affordable legal consultation powered by 
            artificial intelligence. Get instant guidance based on Pakistani law, 
            PPC, and Constitution.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-form-side">
        <div className="auth-container">
          <div className="auth-card">
            {/* Logo Section */}
            <div className="auth-logo">
              <img src={logo} alt="LawBot Logo" />
              <h1 className="auth-title">Create Account</h1>
              <p className="auth-subtitle">Start your legal journey today</p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Success Display */}
            {success && (
              <div className="success-message">
                Account created successfully! Redirecting to login...
              </div>
            )}

            {/* SignUp Form */}
            <Form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  className="form-control"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  className="form-control"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  className="form-control"
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </Form>

            {/* Footer */}
            <div className="auth-footer">
              <p>
                Already have an account?{' '}
                <Link to="/">Login now</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUP;
